export const PALETTE = [
  { type: 'text', label: 'Short Text', icon: 'text' },
  { type: 'textarea', label: 'Long Text', icon: 'textarea' },
  { type: 'email', label: 'Email', icon: 'email' },
  { type: 'phone', label: 'Phone', icon: 'phone' },
  { type: 'select', label: 'Dropdown', icon: 'select' },
  { type: 'radio', label: 'Multiple Choice', icon: 'radio' },
  { type: 'checkbox', label: 'Checkbox', icon: 'checkbox' },
  { type: 'file', label: 'File Upload', icon: 'file' },
  { type: 'date', label: 'Date', icon: 'date' },
  { type: 'url', label: 'URL', icon: 'url' },
  { type: 'heading', label: 'Heading', icon: 'heading' },
  { type: 'divider', label: 'Divider', icon: 'divider' },
  { type: 'paragraph', label: 'Paragraph', icon: 'paragraph' },
];

export const LAYOUT_TYPES = ['heading', 'divider', 'paragraph'];
export const CHOICE_TYPES = ['select', 'radio', 'checkbox'];

export const CATEGORIES = [
  { id: 'website_form', label: 'Website Form', plural: 'Website Forms' },
  { id: 'questionnaire', label: 'Questionnaire', plural: 'Questionnaires' },
  { id: 'client_request', label: 'Client Request', plural: 'Client Requests' },
  { id: 'template', label: 'Template', plural: 'Templates' },
];

export const AUDIENCES = [
  { id: 'website', label: 'Website form', hint: 'Add to your website for leads, quotes or bookings.' },
  { id: 'client', label: 'Send to a client', hint: 'Share with a specific client or project.' },
  { id: 'project', label: 'Add to a project', hint: 'Use internally for project tasks.' },
  { id: 'internal', label: 'Internal use', hint: 'For team use only.' },
];

export const STATUS_META = {
  active: { label: 'Active', tone: 'green' },
  complete: { label: 'Complete', tone: 'green' },
  waiting: { label: 'Waiting', tone: 'amber' },
  in_progress: { label: 'In progress', tone: 'amber' },
  overdue: { label: 'Past due', tone: 'red' },
  draft: { label: 'Draft', tone: 'gray' },
  closed: { label: 'Closed', tone: 'gray' },
  template: { label: 'Template', tone: 'slate' },
};

export const SORTS = [
  { id: 'updated', label: 'Last updated' },
  { id: 'created', label: 'Newest' },
  { id: 'name', label: 'Name (A–Z)' },
  { id: 'submissions', label: 'Most submissions' },
  { id: 'last_response', label: 'Last response' },
  { id: 'due', label: 'Due date' },
];

export const DEFAULT_FILE_TYPES = ['png', 'jpg', 'pdf', 'ai'];

export function categoryLabel(id) {
  return CATEGORIES.find((c) => c.id === id)?.label || 'Form';
}

export function paletteItem(type) {
  return PALETTE.find((p) => p.type === type) || PALETTE[0];
}

export function slugKey(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

function randomId() {
  return Math.random().toString(36).slice(2, 12);
}

export function uniqueKey(base, fields, ignoreId) {
  const root = slugKey(base) || 'field';
  const taken = new Set(fields.filter((f) => f.id !== ignoreId).map((f) => f.key));
  let key = root;
  let n = 2;
  while (taken.has(key)) key = `${root}_${n++}`;
  return key;
}

const DEFAULT_LABELS = {
  text: 'Short answer',
  textarea: 'Long answer',
  email: 'Email address',
  phone: 'Phone number',
  select: 'Choose one',
  radio: 'Pick an option',
  checkbox: 'Select all that apply',
  file: 'Upload a file',
  date: 'Date',
  url: 'Website',
  heading: 'Section heading',
  divider: 'Divider',
  paragraph: 'Paragraph',
};

const DEFAULT_PLACEHOLDERS = {
  text: '',
  textarea: '',
  email: 'you@company.com',
  phone: '(555) 123-4567',
  url: 'https://',
};

export function blankField(type, fields) {
  const label = DEFAULT_LABELS[type] || 'Untitled question';
  return {
    id: randomId(),
    key: uniqueKey(label, fields),
    type,
    label,
    placeholder: DEFAULT_PLACEHOLDERS[type] || '',
    help: '',
    text: type === 'paragraph' ? 'Add some helpful context for the person filling this in.' : '',
    required: false,
    options: CHOICE_TYPES.includes(type) ? ['Option 1', 'Option 2', 'Option 3'] : [],
    accept: type === 'file' ? [...DEFAULT_FILE_TYPES] : [],
    max_mb: type === 'file' ? 10 : 0,
    multiple: false,
  };
}

export function duplicateField(field, fields) {
  return { ...field, id: randomId(), key: uniqueKey(field.key, fields), label: `${field.label} (copy)` };
}

export function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatDay(value) {
  if (!value) return '—';
  const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function lastResponseLabel(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return `Today, ${formatTime(value)}`;
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `Yesterday, ${formatTime(value)}`;
  return formatDay(value);
}

export function formatBytes(size) {
  if (!size) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
